import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createConnectionWatchdog, type WatchdogState } from '@/lib/connectionWatchdog';
import { createReconnectController } from '@/lib/socketResilience';
import {
  decryptRoomContent,
  encryptRoomContent,
  getRoomSecret,
  forgetRoomSecret,
} from '@/lib/roomCrypto';

export type ChatMessageEvent = {
  type: 'message';
  from: string;
  content: string;
  timestamp: string;
  id?: string;
};

export type PrivateMessageEvent = {
  type: 'private_message';
  from: string;
  to: string;
  content: string;
  timestamp: string;
  id?: string;
};

export type ChatEvent =
  | ChatMessageEvent
  | PrivateMessageEvent
  | { type: 'join'; username: string; participant_count: number }
  | { type: 'leave'; username: string; participant_count: number }
  | { type: 'typing'; username: string }
  | { type: 'stop_typing'; username: string }
  | { type: 'participants'; count: number; names: string[] }
  | { type: 'room_closed'; reason?: string }
  | {
      type: 'welcome';
      participant_id: string;
      resume_token?: string;
      reconnected?: boolean;
      room_state?: string;
    }
  | { type: 'pong' };

export type DisplayMessage =
  | ChatMessageEvent
  | PrivateMessageEvent
  | { type: 'system'; content: string; id: string };

const MAX_MESSAGES = 500;
const PING_INTERVAL_MS = 30000;
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;
const RECONNECT_MAX_ATTEMPTS = 20;

type ResumeCredentials = {
  participantId: string;
  resumeToken: string;
};

function generateId(): string {
  if (
    globalThis.crypto &&
    typeof globalThis.crypto.randomUUID === 'function'
  ) {
    return globalThis.crypto.randomUUID();
  }

  return (
    Date.now().toString(36) +
    '-' +
    Math.random().toString(36).slice(2, 10)
  );
}

/*
 * ROOM resume credentials are intentionally MEMORY-ONLY.
 *
 * Never use localStorage/sessionStorage/cookies for participant
 * credentials. Each mounted useChat instance gets its own
 * participant identity, so two tabs/devices in the same room
 * are independent participants.
 */
export function useChat(roomId: string, name: string) {
  const roomSecret = getRoomSecret(roomId);

  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [participants, setParticipants] = useState<string[]>([]);
  const [participantCount, setParticipantCount] = useState(0);
  const [typingUsers, setTypingUsers] = useState<Set<string>>(new Set());
  const [isClosed, setIsClosed] = useState(false);

  const [connectionStatus, setConnectionStatus] = useState<
    'connecting' | 'connected' | 'reconnecting' | 'disconnected'
  >('connecting');

  const [watchdogState, setWatchdogState] =
    useState<WatchdogState>('INIT');

  const wsRef = useRef<WebSocket | null>(null);

  // IMPORTANT:
  // Resume credentials live only in this hook instance's memory.
  // They are never persisted in localStorage/sessionStorage.
  // This guarantees that two clients opening the same room get
  // different participant identities.
  const resumeRef = useRef<ResumeCredentials | null>(null);
  const resumeRoomIdRef = useRef(roomId);

  if (resumeRoomIdRef.current !== roomId) {
    resumeRoomIdRef.current = roomId;
    resumeRef.current = null;
  }

  const generationRef = useRef(0);
  const intentionalCloseRef = useRef(false);
  const destroyedRef = useRef(false);
  const connectInFlightRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);

  // Strict single-socket / single-reconnect guard.
  // Prevents online/visibility/onclose from opening concurrent sockets.
  const reconnectScheduledRef = useRef(false);

  const reconnectControllerRef = useRef<
    ReturnType<typeof createReconnectController> | null
  >(null);

  const watchdogRef = useRef<
    ReturnType<typeof createConnectionWatchdog> | null
  >(null);

  const pingIntervalRef = useRef<number | null>(null);
  const reconnectKickRef = useRef<number | null>(null);
  const lastPongRef = useRef(Date.now());

  const typingTimeoutsRef = useRef<Record<string, number>>({});

  const connectRef = useRef<() => void>(() => undefined);

  const addMessage = useCallback((msg: DisplayMessage) => {
    setMessages((prev) => {
      if (msg.id && prev.some((m) => m.id === msg.id)) {
        return prev;
      }

      const next = [...prev, msg];

      return next.length > MAX_MESSAGES
        ? next.slice(-MAX_MESSAGES)
        : next;
    });
  }, []);

  const clearTimers = useCallback(() => {
    if (pingIntervalRef.current !== null) {
      window.clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    if (reconnectKickRef.current !== null) {
      window.clearTimeout(reconnectKickRef.current);
      reconnectKickRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    if (
      !roomId ||
      !name ||
      isClosed ||
      destroyedRef.current ||
      intentionalCloseRef.current ||
      connectInFlightRef.current
    ) {
      return;
    }

    const current = wsRef.current;

    // Never create a second socket while any previous socket is
    // still alive, including the CLOSING state.
    if (
      current &&
      current.readyState !== WebSocket.CLOSED
    ) {
      return;
    }

    // A connection attempt is already being created.
    if (connectInFlightRef.current) {
      return;
    }

    connectInFlightRef.current = true;

    // This connect consumes the pending reconnect request.
    reconnectScheduledRef.current = false;

    const generation = ++generationRef.current;

    const reconnecting =
      reconnectAttemptsRef.current > 0;

    setConnectionStatus(
      reconnecting ? 'reconnecting' : 'connecting',
    );

    const protocol =
      window.location.protocol === 'https:'
        ? 'wss:'
        : 'ws:';

    const host = window.location.host;

    const resume = resumeRef.current;

    const params = new URLSearchParams();

    params.set('name', name);

    if (resume) {
      params.set(
        'participant_id',
        resume.participantId,
      );

      params.set(
        'resume_token',
        resume.resumeToken,
      );
    }

    const ws = new WebSocket(
      `${protocol}//${host}/api/chat/ws/${encodeURIComponent(
        roomId,
      )}?${params.toString()}`,
    );

    wsRef.current = ws;

    const isCurrentSocket = () =>
      generation === generationRef.current &&
      wsRef.current === ws;

    ws.onopen = () => {
      if (
        !isCurrentSocket() ||
        destroyedRef.current ||
        intentionalCloseRef.current
      ) {
        ws.close(1000, 'stale socket');
        return;
      }

      connectInFlightRef.current = false;
      reconnectAttemptsRef.current = 0;

      reconnectControllerRef.current?.reset();

      lastPongRef.current = Date.now();

      setConnectionStatus('connected');

      watchdogRef.current?.connected({
        reason: 'socket-open',
      });

      watchdogRef.current?.markActivity({
        reason: 'socket-open',
      });

      if (pingIntervalRef.current !== null) {
        window.clearInterval(
          pingIntervalRef.current,
        );
      }

      pingIntervalRef.current =
        window.setInterval(() => {
          if (
            !isCurrentSocket() ||
            ws.readyState !== WebSocket.OPEN
          ) {
            return;
          }

          if (
            Date.now() - lastPongRef.current >
            INACTIVITY_TIMEOUT_MS
          ) {
            ws.close(
              4000,
              'heartbeat timeout',
            );

            return;
          }

          try {
            ws.send(
              JSON.stringify({
                type: 'ping',
              }),
            );
          } catch {
            // onclose handles reconnection.
          }
        }, PING_INTERVAL_MS);
    };

    ws.onmessage = async (event) => {
      if (
        !isCurrentSocket() ||
        destroyedRef.current
      ) {
        return;
      }

      try {
        const data = JSON.parse(
          event.data,
        ) as ChatEvent;

        switch (data.type) {
          case 'welcome':
            if (
              data.participant_id &&
              data.resume_token
            ) {
              resumeRef.current = {
                participantId: data.participant_id,
                resumeToken: data.resume_token,
              };
            }
            break;

          case 'message':
          case 'private_message': {
            if (!roomSecret) break;

            try {
              const content =
                await decryptRoomContent(
                  data.content,
                  roomSecret,
                );

              if (
                !isCurrentSocket() ||
                destroyedRef.current
              ) {
                return;
              }

              addMessage({
                ...data,
                content,
                id:
                  data.id ||
                  generateId(),
              });
            } catch (error) {
              console.error(
                'Failed to decrypt ROOM message:',
                error,
              );
            }

            break;
          }

          case 'join':
            setParticipantCount(
              data.participant_count,
            );

            addMessage({
              type: 'system',
              content: `${data.username} joined`,
              id: generateId(),
            });

            break;

          case 'leave':
            setParticipantCount(
              data.participant_count,
            );

            addMessage({
              type: 'system',
              content: `${data.username} left`,
              id: generateId(),
            });

            setTypingUsers((prev) => {
              const next = new Set(prev);

              next.delete(data.username);

              return next;
            });

            break;

          case 'participants':
            setParticipantCount(data.count);
            setParticipants([...data.names]);
            break;

          case 'typing':
            if (data.username !== name) {
              setTypingUsers((prev) => {
                const next = new Set(prev);

                next.add(data.username);

                return next;
              });

              if (
                typingTimeoutsRef.current[
                  data.username
                ]
              ) {
                window.clearTimeout(
                  typingTimeoutsRef.current[
                    data.username
                  ],
                );
              }

              typingTimeoutsRef.current[
                data.username
              ] = window.setTimeout(() => {
                setTypingUsers((prev) => {
                  const next = new Set(prev);

                  next.delete(data.username);

                  return next;
                });

                delete typingTimeoutsRef.current[
                  data.username
                ];
              }, 3000);
            }

            break;

          case 'stop_typing':
            setTypingUsers((prev) => {
              const next = new Set(prev);

              next.delete(data.username);

              return next;
            });

            if (
              typingTimeoutsRef.current[
                data.username
              ]
            ) {
              window.clearTimeout(
                typingTimeoutsRef.current[
                  data.username
                ],
              );

              delete typingTimeoutsRef.current[
                data.username
              ];
            }

            break;

          case 'room_closed':
            intentionalCloseRef.current = true;

            setIsClosed(true);

            clearTimers();

            reconnectScheduledRef.current = false;

            reconnectControllerRef.current?.cancel();

            resumeRef.current = null;

            forgetRoomSecret(roomId);

            ws.close(
              1000,
              'room closed',
            );

            break;

          case 'pong':
            lastPongRef.current = Date.now();

            watchdogRef.current?.markActivity({
              reason: 'pong',
            });

            break;

          default:
            break;
        }
      } catch (error) {
        console.error(
          'Failed to process ROOM WebSocket event:',
          error,
        );
      }
    };

    ws.onclose = (event) => {
      if (!isCurrentSocket()) return;

      clearTimers();

      wsRef.current = null;

      connectInFlightRef.current = false;

      if (
        intentionalCloseRef.current ||
        destroyedRef.current ||
        isClosed
      ) {
        return;
      }

      watchdogRef.current?.disconnected({
        reason: event.wasClean
          ? 'closed-cleanly'
          : 'closed-uncleanly',
        detail: `code:${event.code}`,
      });

      setConnectionStatus('disconnected');

      // Only one reconnect may be queued at a time.
      if (reconnectScheduledRef.current) {
        return;
      }

      reconnectScheduledRef.current = true;

      const scheduled =
        reconnectControllerRef.current?.schedule(
          () => {
            reconnectScheduledRef.current = false;

            if (
              !destroyedRef.current &&
              !intentionalCloseRef.current &&
              !isClosed &&
              !wsRef.current &&
              !connectInFlightRef.current
            ) {
              connectRef.current();
            }
          },
        ) ?? false;

      if (scheduled) {
        reconnectAttemptsRef.current += 1;

        setConnectionStatus(
          'reconnecting',
        );
      } else {
        reconnectScheduledRef.current = false;
        reconnectAttemptsRef.current = 0;
      }
    };

    ws.onerror = () => {
      if (!isCurrentSocket()) return;

      watchdogRef.current?.error({
        reason: 'socket-error',
        detail: 'WebSocket error',
      });
    };
  }, [
    roomId,
    name,
    roomSecret,
    isClosed,
    addMessage,
    clearTimers,
  ]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  useEffect(() => {
    destroyedRef.current = false;
    intentionalCloseRef.current = false;

    reconnectAttemptsRef.current = 0;

    setIsClosed(false);

    watchdogRef.current =
      createConnectionWatchdog({
        label: `chat:${roomId || 'room'}`,
      });

    reconnectControllerRef.current =
      createReconnectController({
        baseDelayMs: 1500,
        maxDelayMs: 15000,
        maxAttempts:
          RECONNECT_MAX_ATTEMPTS,
      });

    const unsubscribe =
      watchdogRef.current.subscribe(
        setWatchdogState,
      );

    watchdogRef.current.start();

    const onlineHandler = () => {
      if (
        destroyedRef.current ||
        intentionalCloseRef.current
      ) {
        return;
      }

      reconnectControllerRef.current?.reset();

      reconnectAttemptsRef.current = 0;

      if (
        reconnectKickRef.current !== null
      ) {
        window.clearTimeout(
          reconnectKickRef.current,
        );
      }

      reconnectKickRef.current =
        window.setTimeout(() => {
          reconnectKickRef.current = null;

          if (
            destroyedRef.current ||
            intentionalCloseRef.current ||
            isClosed ||
            wsRef.current ||
            connectInFlightRef.current ||
            reconnectScheduledRef.current
          ) {
            return;
          }

          connectRef.current();
        }, 500);
    };

    const visibilityHandler = () => {
      if (
        document.visibilityState !==
        'visible'
      ) {
        return;
      }

      if (
        destroyedRef.current ||
        intentionalCloseRef.current
      ) {
        return;
      }

      if (
        wsRef.current?.readyState ===
        WebSocket.OPEN
      ) {
        return;
      }

      if (
        reconnectKickRef.current !== null
      ) {
        window.clearTimeout(
          reconnectKickRef.current,
        );
      }

      reconnectKickRef.current =
        window.setTimeout(() => {
          reconnectKickRef.current =
            null;

          if (
            destroyedRef.current ||
            intentionalCloseRef.current ||
            isClosed ||
            wsRef.current ||
            connectInFlightRef.current ||
            reconnectScheduledRef.current
          ) {
            return;
          }

          reconnectControllerRef.current?.reset();

          reconnectAttemptsRef.current = 0;

          connectRef.current();
        }, 750);
    };

    window.addEventListener(
      'online',
      onlineHandler,
    );

    document.addEventListener(
      'visibilitychange',
      visibilityHandler,
    );

    connectRef.current();

    return () => {
      destroyedRef.current = true;

      generationRef.current += 1;

      window.removeEventListener(
        'online',
        onlineHandler,
      );

      document.removeEventListener(
        'visibilitychange',
        visibilityHandler,
      );

      clearTimers();

      if (wsRef.current) {
        const ws = wsRef.current;

        wsRef.current = null;

        try {
          ws.close(
            1000,
            'component cleanup',
          );
        } catch {
          // Nothing to do.
        }
      }

      reconnectControllerRef.current?.cancel();

      reconnectControllerRef.current = null;

      watchdogRef.current?.stop();

      watchdogRef.current = null;

      unsubscribe();

      Object.values(
        typingTimeoutsRef.current,
      ).forEach(window.clearTimeout);

      typingTimeoutsRef.current = {};

      reconnectScheduledRef.current = false;
      connectInFlightRef.current = false;
    };
  }, [
    roomId,
    name,
    clearTimers,
  ]);

  /**
   * Explicit voluntary room closure.
   *
   * IMPORTANT:
   * - Android backgrounding does NOT call this.
   * - network interruptions do NOT call this.
   * - visibilitychange does NOT call this.
   * - WebSocket onclose does NOT call this.
   *
   * The local credentials are deleted ONLY after the
   * Gateway confirms the permanent room closure.
   */
  const leaveRoom = useCallback(async () => {
    if (!roomId) return;

    intentionalCloseRef.current = true;
    destroyedRef.current = true;

    generationRef.current += 1;

    clearTimers();

    reconnectScheduledRef.current = false;

    reconnectControllerRef.current?.cancel();

    const ws = wsRef.current;

    wsRef.current = null;

    if (ws) {
      try {
        ws.close(
          1000,
          'User left room',
        );
      } catch {
        // Nothing to do.
      }
    }

    const resume = resumeRef.current;

    if (!resume) {
      console.error(
        '[ROOM] Cannot close room: resume credentials missing.',
      );

      intentionalCloseRef.current = false;
      destroyedRef.current = false;

      return;
    }

    try {
      const response = await fetch(
        `/api/chat/rooms/${encodeURIComponent(
          roomId,
        )}/close`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            participant_id:
              resume.participantId,
            resume_token:
              resume.resumeToken,
          }),
          keepalive: true,
        },
      );

      const responseText =
        await response.text();

      console.log(
        '[ROOM] close response:',
        response.status,
        responseText,
      );

      if (!response.ok) {
        throw new Error(
          `ROOM close failed: HTTP ${response.status}`,
        );
      }

      /*
       * CRITICAL:
       *
       * We only destroy the local resume credentials
       * AFTER the Gateway confirmed the permanent close.
       */
      resumeRef.current = null;
      forgetRoomSecret(roomId);

      setIsClosed(true);
      setConnectionStatus(
        'disconnected',
      );
    } catch (error) {
      /*
       * The room was NOT confirmed closed.
       *
       * Do NOT delete resume credentials.
       * Do NOT delete the E2EE secret.
       *
       * This prevents the user from losing access to
       * a room that still exists on the Gateway.
       */
      console.error(
        '[ROOM] Permanent close failed:',
        error,
      );

      /*
       * Allow the caller to handle the failure.
       * The room credentials remain available.
       */
      intentionalCloseRef.current = false;
      destroyedRef.current = false;

      setConnectionStatus(
        'disconnected',
      );

      throw error;
    }
  }, [
    roomId,
    clearTimers,
  ]);

  const sendMessage = useCallback(
    async (content: string) => {
      if (
        !roomSecret ||
        wsRef.current?.readyState !==
          WebSocket.OPEN
      ) {
        return;
      }

      try {
        const encrypted =
          await encryptRoomContent(
            content,
            roomSecret,
          );

        wsRef.current.send(
          JSON.stringify({
            type: 'message',
            content: encrypted,
          }),
        );

        wsRef.current.send(
          JSON.stringify({
            type: 'stop_typing',
          }),
        );
      } catch (error) {
        console.error(
          'Failed to encrypt message:',
          error,
        );
      }
    },
    [roomSecret],
  );

  const sendPrivateMessage = useCallback(
    async (
      to: string,
      content: string,
    ) => {
      if (
        !roomSecret ||
        wsRef.current?.readyState !==
          WebSocket.OPEN
      ) {
        return;
      }

      try {
        const encrypted =
          await encryptRoomContent(
            content,
            roomSecret,
          );

        wsRef.current.send(
          JSON.stringify({
            type: 'private_message',
            to,
            content: encrypted,
          }),
        );

        wsRef.current.send(
          JSON.stringify({
            type: 'stop_typing',
          }),
        );
      } catch (error) {
        console.error(
          'Failed to encrypt private message:',
          error,
        );
      }
    },
    [roomSecret],
  );

  const sendTyping = useCallback(() => {
    if (
      wsRef.current?.readyState ===
      WebSocket.OPEN
    ) {
      wsRef.current.send(
        JSON.stringify({
          type: 'typing',
        }),
      );
    }
  }, []);

  const sendStopTyping = useCallback(() => {
    if (
      wsRef.current?.readyState ===
      WebSocket.OPEN
    ) {
      wsRef.current.send(
        JSON.stringify({
          type: 'stop_typing',
        }),
      );
    }
  }, []);

  const memoizedTypingUsers =
    useMemo(
      () =>
        Array.from(typingUsers),
      [typingUsers],
    );

  const start = useCallback(
    () =>
      watchdogRef.current?.start() ??
      'INIT',
    [],
  );

  const stop = useCallback(
    () =>
      watchdogRef.current?.stop() ??
      'DISCONNECTED',
    [],
  );

  const state = useCallback(
    () =>
      watchdogRef.current?.state() ??
      'INIT',
    [],
  );

  const getMetrics = useCallback(
    () =>
      watchdogRef.current?.getMetrics() ?? {
        state: 'INIT' as WatchdogState,
        lastActivityAt: Date.now(),
        eventCount: 0,
        heartbeatCount: 0,
        errorCount: 0,
        lastTransitionAt: Date.now(),
      },
    [],
  );

  return {
    messages,
    participants,
    participantCount,
    typingUsers:
      memoizedTypingUsers,
    isClosed,
    connectionStatus,
    watchdogState,
    start,
    stop,
    state,
    getMetrics,
    sendMessage,
    sendPrivateMessage,
    sendTyping,
    sendStopTyping,
    leaveRoom,
    maxReconnectAttempts:
      RECONNECT_MAX_ATTEMPTS,
  };
}
